
import { v4 as uuidv4 } from "uuid";
import { 
  ResourceFeature,
  UpgradableNumber,
  CharacterSheetInterface,
  CharacterSlot
} from "@dndModels";
import { LEVELED_SPELL_LEVELS } from "@dndModels/Constants";
import { SpellLevel } from "@dndModels/Types";
import DataUtilities from "@dndUtilities/data_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

const data_util = DataUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

/**
 * This is a generic term for Spell Slots, Ki Points, Sorcery Points,
 * Lay on Hands, Healing Light, etc.  
 * It also could work for Material Spell Components, but I don't 
 * want to worry about that right now.
 * level is for spell slots (and pact slots), 
 * and it's what level the slot is.
 * total is the total number of that resource they have.
 * used is how many of them are currently used.
 * refreshRule is when this refreshes (generally short or long).
 */
export class CharacterResource {
  true_id = "";
  type_id = "";
  level = 1;
  size = 1;
  true_total = 0;
  name = "";
  singular = "";
  get use_singular() {
    return this.singular.length > 0 ? this.singular : this.name;
  }
  resource_feature: ResourceFeature | null = null;
  class_id = "";
  source_name = "";
  _refresh_rule = "";
  show_source_name = false;

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.type_id = obj.type_id ? obj.type_id : "";
      this.level = obj.level !== undefined ? obj.level : 1;
      this.size = obj.size !== undefined ? obj.size : 1;
      this.true_total = obj.total ? +obj.total : 0;
      this.name = obj.name ? obj.name : "";
    }
    this.resource_feature = null;
    this.class_id = "";
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      type_id: this.type_id,
      level: this.level,
      size: this.size,
      total: this.true_total,
      _refresh_rule: this._refresh_rule
    };
  }

  copyResource(copyMe: ResourceFeature, 
    char: CharacterSheetInterface, 
    source_id: string, 
    source_name: string) {
    this.resource_feature = copyMe;
    this.class_id = source_id;
    this.source_name = source_name;
    if (copyMe.type_id) {
      this.type_id = copyMe.type_id;
      if (!copyMe.resource) {
        copyMe.resource = data_util.resources.find(o => o._id === this.type_id);
      }
      this.name = copyMe.override_name !== "" ? copyMe.override_name : (copyMe.resource ? copyMe.resource.name : this.name);
      this.singular = copyMe.override_singular !== "" ? copyMe.override_singular : (copyMe.override_name !== "" ? copyMe.override_name : (copyMe.resource ? copyMe.resource.singular : this.singular));
      if (copyMe.stacks) {
        // debugger;
        this.true_total += char_util.value(copyMe.total, { obj: char, source_id });
        this.size = Math.max(this.size, char_util.value(copyMe.size, { obj: char, source_id }));
      } else {
        this.true_total = Math.max(this.true_total, char_util.value(copyMe.total, { obj: char, source_id }));
        this.size = Math.max(this.size, char_util.value(copyMe.size, { obj: char, source_id }));
      }
    }
  }

  get total(): number {
    return this.true_total;
  }

  set total(value: number) {
    this.true_total = value;
  }

  get refresh_rule(): string {
    // if (this.name.includes("Ki Po")) {
    //   console.log(this);
    // }
    if (this._refresh_rule !== "") {
      return this._refresh_rule;
    }
    if (this.resource_feature) {
      if (this.resource_feature.refresh_rule === "Inherit") {
        if (this.resource_feature.resource) {
          return this.resource_feature.resource.refresh_rule;
        }
      } else {
        return this.resource_feature.refresh_rule;
      }
    }
    return "Long Rest";
  }

  get refresh_x_on_short(): UpgradableNumber {
    if (this.resource_feature) {
      if (this.resource_feature.refresh_x_on_short.isBasic() && this.resource_feature.refresh_x_on_short.base === -1) {
        if (this.resource_feature.resource) {
          return this.resource_feature.resource.refresh_x_on_short;
        }
      } else {
        return this.resource_feature.refresh_x_on_short;
      }
    }
    return new UpgradableNumber();
  }

  get zero_on_rest() {
    return this.resource_feature?.resource?.zero_on_rest??false;
  }

  get is_slot() {
    return this.resource_feature?.slots??false;
  }

  clone(): CharacterResource {
    const clone = new CharacterResource();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CharacterResource): void {
    this.true_id = copyMe.true_id;
    this.type_id = copyMe.type_id;
    this.level = copyMe.level;
    this.size = copyMe.size;
    this.true_total = copyMe.true_total;
    this.name = copyMe.name;
    this.resource_feature = copyMe.resource_feature ? copyMe.resource_feature : null;
    this.class_id = copyMe.class_id;
    this._refresh_rule = copyMe._refresh_rule;
  }
}

export class CharacterResourceWithUsed extends CharacterResource {
  true_used = 0;
  true_created = 0;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.true_used = obj.used ? +obj.used : 0;
      this.true_created = obj.created ? +obj.created : 0;
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      used: this.true_used,
      created: this.true_created,
    };
  }

  get total(): number {
    return this.true_total + this.created;
  }

  set total(value: number) {
    this.true_total = value;
  }

  get created(): number {
    return this.true_created;
  }

  set created(value: number) {
    if (this.true_used > 0) {
      this.true_used -= value;
      if (this.true_used < 0) {
        this.true_created = -1 * this.true_used;
        this.true_used = 0;
      }
    } else {
      this.true_created = value;
    }
  }

  get used(): number {
    return this.true_used;
  }

  set used(value: number) {
    if (this.true_created > 0) {
      this.true_created -= value;
      if (this.true_created < 0) {
        this.true_used = -1 * this.true_created;
        this.true_created = 0;
      }
    } else {
      this.true_used = value;
    }
  }

  get available(): number {
    return this.total - this.used;
  }

  get true_available(): number {
    return this.true_total - this.true_used;
  }

  get slot() {
    const slot = new CharacterSlot();
    const resource_feature = this.resource_feature;
    if (resource_feature) {
      slot.slot_name = resource_feature.slot_name === "" ? resource_feature.use_name : resource_feature.slot_name;
    }
    slot.level = this.resource_feature?.slot_level??1;
    if (slot.level === -1 && LEVELED_SPELL_LEVELS.includes(`${this.size}`)) {
      slot.level = this.size as SpellLevel;
    }
    slot.total = this.total;
    slot.used = this.used;
    slot.type_id = this.type_id;
    slot.resource_true_id = this.true_id;
    return slot;
  }

  clone(): CharacterResourceWithUsed {
    const clone = new CharacterResourceWithUsed();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CharacterResourceWithUsed): void {
    super.copy(copyMe);
    this.true_used = copyMe.true_used;
    this.true_created = copyMe.true_created;
  }
}